/**
 * Generic Scoring Engine for View Profiles
 *
 * A profile-driven way to classify nodes and edges into severity buckets
 * (critical, high, medium, low) and paint them with colors. Used by any
 * domain's view profile system — genogram perspectives, orgchart lenses,
 * network security/cost overlays, etc.
 *
 * A profile declares a list of scoring rules. Each rule targets nodes or
 * edges matching a predicate and assigns a score (severity + color + label).
 * The engine walks all nodes/edges against all rules and produces a
 * `ScoringResult` — a map of id → scores. Rules are additive: a single node
 * can match multiple rules, and the engine tracks them all.
 *
 * Chain rules (rule.type === 'chain') are BFS walks over specific edge
 * types. They're used for things like "highlight the path from public
 * internet to data stores" (security profile) or "transitive blast radius
 * from this resource" (operations profile).
 *
 * Profiles are domain-agnostic — the engine doesn't know what `postgres`
 * or `person` mean, it just evaluates predicates over node/edge props.
 * Domains provide the predicates.
 */

import type { GraphNode, GraphEdge } from '../graph/types';
import { severityColor } from './severityPalette';

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/**
 * Predicate over a node or edge. Receives the object and its props bag;
 * returns true if the rule matches. Kept as a function (not a JSON expression)
 * because domain-specific checks (`isA`, `containsPII`, etc.) are trivial to
 * write as plain TypeScript and hard to serialize cleanly.
 */
export type NodePredicate = (node: GraphNode, props: Record<string, unknown>) => boolean;
export type EdgePredicate = (edge: GraphEdge, props: Record<string, unknown>) => boolean;

/**
 * A single scoring rule. When `when` matches a node/edge, the engine
 * records the rule against that object.
 */
export interface ScoringRule {
  /** Unique ID within the profile — used for legend keying. */
  id: string;
  /** Optional category for grouping in the legend. */
  category?: string;
  /** Short display label ("Publicly exposed", "Unencrypted PII"). */
  label: string;
  /** Severity for sorting and color-fallback. */
  severity: Severity;
  /**
   * Optional hex color override. If omitted, the engine resolves color from
   * `SEVERITY_PALETTE[severity]` so the visual language stays consistent
   * across profiles. Set explicitly only when severity alone is wrong
   * (e.g. cost's "low" bucket using green to read as "fine, not waste").
   */
  color?: string;
  /** Longer description shown in tooltips. */
  description?: string;
  /** Applies to nodes, edges, or both. */
  target: 'node' | 'edge' | 'both';
  /**
   * Rule kind:
   * - 'simple' — predicate-based, runs once per node/edge
   * - 'chain'  — BFS walk from nodes matching `from` via edges matching
   *              `via` to nodes matching `to`. Marks every node/edge along
   *              the path with this rule.
   */
  type?: 'simple' | 'chain';
  /** Predicate for simple node rules (ignored for edge rules). */
  whenNode?: NodePredicate;
  /** Predicate for simple edge rules (ignored for node rules). */
  whenEdge?: EdgePredicate;
  /** For chain rules: predicate matching the starting node. */
  from?: NodePredicate;
  /** For chain rules: edge types or a predicate the chain walks through. */
  via?: string[] | EdgePredicate;
  /** For chain rules: predicate matching the terminating node. */
  to?: NodePredicate;
}

/**
 * Extended view profile that layers scoring rules on top of visibility.
 * Compatible with the base ViewProfile in graph/types.ts — any existing
 * profile can opt in to scoring by adding a `scoring` array.
 */
export interface ScoringProfile {
  id: string;
  name: string;
  description?: string;
  /** Which domain this profile belongs to — used for registry lookup. */
  domain: string;
  /** If true, this profile is the domain default. */
  default?: boolean;
  /**
   * Visibility filters. Empty/omitted = show everything. Rules still
   * evaluate against hidden nodes, but they don't appear in the legend.
   */
  visibility?: {
    nodeTypes?: string[];
    edgeTypes?: string[];
    fieldPacks?: string[];
  };
  /** Scoring rules applied to nodes/edges. */
  scoring: ScoringRule[];
  /** Optional static palette (node type → color) applied before scoring. */
  palette?: Record<string, string>;
  /** Optional default node color when no rules match. */
  defaultColor?: string;
}

/**
 * Result for a single node or edge after all rules have run. The `rules`
 * array is ordered by severity (most critical first) so UI can pick the
 * top N for badges or color.
 */
export interface ScoredItem {
  id: string;
  /** All rules that matched this item, sorted most → least severe. */
  rules: ScoringRule[];
  /** The highest-severity rule's color, for quick color lookup. */
  topColor?: string;
  /** The highest severity across matched rules. */
  topSeverity?: Severity;
}

export interface ScoringResult {
  /** Scored nodes keyed by id. Only contains nodes that matched ≥1 rule. */
  nodes: Map<string, ScoredItem>;
  /** Scored edges keyed by id. Only contains edges that matched ≥1 rule. */
  edges: Map<string, ScoredItem>;
  /** Per-rule match count — drives the legend "N matches" subtitle. */
  counts: Map<string, number>;
}

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

function compareSeverity(a: Severity | undefined, b: Severity | undefined): number {
  return (SEVERITY_RANK[a ?? 'info'] ?? 99) - (SEVERITY_RANK[b ?? 'info'] ?? 99);
}

function recordMatch(result: ScoringResult, kind: 'node' | 'edge', id: string, rule: ScoringRule): void {
  const map = kind === 'node' ? result.nodes : result.edges;
  let entry = map.get(id);
  if (!entry) {
    entry = { id, rules: [] };
    map.set(id, entry);
  }
  entry.rules.push(rule);
  result.counts.set(rule.id, (result.counts.get(rule.id) ?? 0) + 1);
}

function resolveColor(rule: ScoringRule): string {
  return rule.color ?? severityColor(rule.severity);
}

function finalizeScores(result: ScoringResult): void {
  for (const map of [result.nodes, result.edges]) {
    for (const item of map.values()) {
      item.rules.sort((a, b) => compareSeverity(a.severity, b.severity));
      const top = item.rules[0];
      if (top) {
        item.topColor = resolveColor(top);
        item.topSeverity = top.severity;
      }
    }
  }
}

/**
 * Run the profile's scoring rules over a graph. Pure function — no side
 * effects, safe to call on every render (memoized via useMemo in the UI).
 *
 * Performance: O(nodes × rules + edges × rules + chain_rules × graph_size).
 * At our scale (≤100 nodes per view, ≤10 rules per profile) this is a few
 * thousand predicate evaluations per score pass — sub-millisecond in practice.
 */
export function evaluateProfile(
  profile: ScoringProfile,
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
): ScoringResult {
  const result: ScoringResult = {
    nodes: new Map(),
    edges: new Map(),
    counts: new Map(),
  };

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const edgesByFrom = new Map<string, GraphEdge[]>();
  for (const e of edges) {
    if (!edgesByFrom.has(e.from)) edgesByFrom.set(e.from, []);
    edgesByFrom.get(e.from)!.push(e);
  }

  for (const rule of profile.scoring) {
    const kind = rule.type ?? 'simple';

    if (kind === 'simple') {
      if (rule.target === 'node' || rule.target === 'both') {
        if (rule.whenNode) {
          for (const node of nodes) {
            if (rule.whenNode(node, (node.props ?? {}) as Record<string, unknown>)) {
              recordMatch(result, 'node', node.id, rule);
            }
          }
        }
      }
      if (rule.target === 'edge' || rule.target === 'both') {
        if (rule.whenEdge) {
          for (const edge of edges) {
            if (rule.whenEdge(edge, (edge.props ?? {}) as Record<string, unknown>)) {
              recordMatch(result, 'edge', edge.id, rule);
            }
          }
        }
      }
    } else if (kind === 'chain') {
      // BFS from nodes matching `from` via edges matching `via` to nodes
      // matching `to`. Any node or edge traversed gets tagged with the rule.
      if (!rule.from || !rule.to || !rule.via) continue;
      const viaPred = typeof rule.via === 'function'
        ? rule.via
        : ((edge: GraphEdge) => (rule.via as string[]).includes(edge.type));

      const startNodes = nodes.filter((n) =>
        rule.from!(n, (n.props ?? {}) as Record<string, unknown>),
      );
      for (const start of startNodes) {
        const visited = new Set<string>();
        const queue: Array<{ nodeId: string; path: string[] }> = [{ nodeId: start.id, path: [] }];
        while (queue.length > 0) {
          const { nodeId, path } = queue.shift()!;
          if (visited.has(nodeId)) continue;
          visited.add(nodeId);
          const outgoing = edgesByFrom.get(nodeId) ?? [];
          for (const edge of outgoing) {
            if (!viaPred(edge, (edge.props ?? {}) as Record<string, unknown>)) continue;
            const target = nodeById.get(edge.to);
            if (!target) continue;
            const newPath = [...path, edge.id];
            if (rule.to(target, (target.props ?? {}) as Record<string, unknown>)) {
              // Terminal node reached — tag every node and edge along the path.
              recordMatch(result, 'node', start.id, rule);
              recordMatch(result, 'node', target.id, rule);
              for (const pathEdgeId of newPath) {
                recordMatch(result, 'edge', pathEdgeId, rule);
              }
            } else {
              queue.push({ nodeId: target.id, path: newPath });
            }
          }
        }
      }
    }
  }

  finalizeScores(result);
  return result;
}

/**
 * Convenience: pull the color for a specific node id out of a scoring result.
 * Returns undefined if the node wasn't matched by any rule — caller should
 * fall back to the profile's palette or the shape manifest's default accent.
 */
export function getNodeColor(result: ScoringResult, nodeId: string): string | undefined {
  return result.nodes.get(nodeId)?.topColor;
}

export function getEdgeColor(result: ScoringResult, edgeId: string): string | undefined {
  return result.edges.get(edgeId)?.topColor;
}

/**
 * Build a legend for a profile based on which rules actually matched. Returns
 * one entry per rule that matched ≥1 item, sorted by severity. Rules that
 * didn't match are omitted so the legend stays compact.
 */
export interface LegendEntry {
  ruleId: string;
  label: string;
  color: string;
  severity: Severity;
  matchCount: number;
  category?: string;
  description?: string;
}

export function buildLegend(profile: ScoringProfile, result: ScoringResult): LegendEntry[] {
  const entries: LegendEntry[] = [];
  for (const rule of profile.scoring) {
    const count = result.counts.get(rule.id) ?? 0;
    if (count === 0) continue;
    entries.push({
      ruleId: rule.id,
      label: rule.label,
      color: resolveColor(rule),
      severity: rule.severity,
      matchCount: count,
      category: rule.category,
      description: rule.description,
    });
  }
  entries.sort((a, b) => compareSeverity(a.severity, b.severity));
  return entries;
}
